import React, { useState } from "react";
import { Phone, MessageCircle, Mail, ArrowRight, Loader2 } from "lucide-react";
import { contact } from "../mock";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { useToast } from "../hooks/use-toast";
import { submitCallbackRequest } from "../lib/api";

export default function CtaCallback() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast({ title: "Please fill required fields", description: "Name and phone are required." });
      return;
    }
    try {
      setLoading(true);
      await submitCallbackRequest(form);
      toast({ title: "Request received", description: "We'll call you back shortly." });
      setForm({ name: "", phone: "", email: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(d => d.msg).join(", ") : (detail || err.message || "Please try again later.");
      toast({ title: "Submission failed", description: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-16 md:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="relative bg-neutral-900 rounded-2xl overflow-hidden bg-dotted-dark">
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-orange-500/20 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-orange-500/10 blur-3xl" />
          <div className="relative grid lg:grid-cols-2 gap-10 p-8 md:p-12">
            <div className="text-white">
              <h3 className="font-display text-2xl md:text-3xl uppercase leading-tight">You are 10 minutes away from the help you need</h3>
              <span className="block w-20 h-[3px] bg-orange-500 mt-4" />
              <p className="mt-5 text-neutral-300 leading-relaxed">
                We offer a wide range of Industrial Heaters &amp; Heating Elements — Shortwave IR Heaters and Shortwave IR Heaters with Reflector Box. Get in touch for custom solutions tailored to your needs.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <a href={`tel:${contact.phone}`}>
                  <Button className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-6 py-6 uppercase tracking-wider font-semibold">
                    <Phone size={16} className="mr-2" /> {contact.phone}
                  </Button>
                </a>
                <a href={`https://wa.me/${contact.whatsapp}`} target="_blank" rel="noreferrer">
                  <Button className="bg-green-500 hover:bg-green-600 text-white rounded-full px-6 py-6 uppercase tracking-wider font-semibold">
                    <MessageCircle size={16} className="mr-2" /> WhatsApp
                  </Button>
                </a>
              </div>
              <div className="mt-7">
                <p className="text-orange-400 text-sm font-semibold uppercase tracking-wider">Drop us a mail</p>
                <div className="mt-3 space-y-1">
                  {contact.emails.map(e => (
                    <a key={e} href={`mailto:${e}`} className="flex items-center gap-2 text-neutral-200 hover:text-orange-400 transition-colors text-sm break-all">
                      <Mail size={14} className="text-orange-500 shrink-0" /> {e}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <form onSubmit={submit} className="bg-white rounded-xl p-6 md:p-8 shadow-2xl">
              <h4 className="font-display text-xl uppercase">Request A Call Back</h4>
              <span className="block w-16 h-[3px] bg-orange-500 mt-3 mb-5" />
              <div className="space-y-4">
                <Input placeholder="Your Name *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="h-12" />
                <Input placeholder="Phone Number *" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="h-12" />
                <Input placeholder="Email Address" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="h-12" />
                <Button type="submit" disabled={loading} className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-70 text-white rounded-full h-12 uppercase tracking-wider font-semibold">
                  {loading ? (<><Loader2 size={16} className="mr-2 animate-spin" /> Sending...</>) : (<>Submit Now <ArrowRight size={16} className="ml-2" /></>)}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
